// src/pages/TermsAndConditions.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified base

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [showFullTerms, setShowFullTerms] = useState(false);

  const email = localStorage.getItem("userEmail");

  /* =========================================================
     📜 Handle Agreement Submission
  ========================================================= */
  const handleAgree = async () => {
    if (!email) {
      alert("❌ Missing user email. Please log in again.");
      return;
    }

    try {
      const res = await axios.post(`${API_BASE}/api/customers/accept-terms`, { email });

      if (res.status === 200) {
        navigate("/welcome");
        window.location.reload(); // optional page refresh
      } else {
        alert("❌ Failed to save agreement.");
      }
    } catch (err) {
      console.error("❌ Terms agreement error:", err);
      alert("❌ Failed to submit agreement. Please try again.");
    }
  };

  /* =========================================================
     🧭 UI
  ========================================================= */
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef9ff] to-[#e0e7ff] flex flex-col items-center justify-center px-6 py-12">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 text-center">
          Terms & Conditions
        </h1>
        <p className="text-gray-600 text-sm mb-4 text-center">
          Please review and accept our terms before using the app.
        </p>

        {/* Terms Box */}
        <div className="text-sm text-gray-700 max-h-[300px] overflow-y-auto px-2 leading-6">
          <p className="mb-4">
            Welcome to <strong>Book Your Need (BYN)</strong>. By creating an
            account or using our services, you agree to be bound by the
            following legally binding agreement.
          </p>

          {!showFullTerms ? (
            <div
              className="text-blue-600 font-semibold cursor-pointer underline"
              onClick={() => setShowFullTerms(true)}
            >
              Read Full Terms & Conditions
            </div>
          ) : (
            <>
              <p className="mb-2">
                <strong>1. Platform Role:</strong> BYN is a technology platform
                that connects customers with independent workers for various
                services. BYN does not directly provide any service and is not
                the employer of any service provider.
              </p>

              <p className="mb-2">
                <strong>2. Independent Contractor Relationship:</strong> Workers
                listed on BYN are independent contractors and not employees,
                agents, or representatives of BYN. BYN does not control the
                manner or method of service delivery.
              </p>

              <p className="mb-2">
                <strong>3. Limitation of Liability:</strong> BYN shall not be
                liable for any direct, indirect, incidental, punitive, or
                consequential damages arising out of or relating to your use of
                the platform or any services provided by a third party. This
                includes, but is not limited to, injury, property damage, or
                dissatisfaction with a service.
              </p>

              <p className="mb-2">
                <strong>4. Assumption of Risk:</strong> You acknowledge that
                services booked through BYN may involve risk. You voluntarily
                assume all such risk related to interacting with service
                providers.
              </p>

              <p className="mb-2">
                <strong>5. Payments:</strong> Payments are processed through a
                third-party provider (e.g., Stripe). BYN does not store payment
                information and is not responsible for payment gateway errors.
              </p>

              <p className="mb-2">
                <strong>6. Dispute Resolution:</strong> Any disputes arising
                from services must be reported within 48 hours of job
                completion. BYN will attempt to mediate disputes but is not
                obligated to provide refunds or compensation unless required by
                law.
              </p>

              <p className="mb-2">
                <strong>7. Cancellation & Refunds:</strong> Cancellations made
                within 2 hours of a scheduled job may be subject to a fee.
                Refund eligibility is determined case-by-case.
              </p>

              <p className="mb-2">
                <strong>8. User Conduct:</strong> Users must treat service
                providers respectfully and comply with all applicable laws.
                Harassment, discrimination, or illegal behavior will lead to
                permanent suspension.
              </p>

              <p className="mb-2">
                <strong>9. Data Privacy:</strong> Your information is securely
                stored and used only for service provision and platform
                improvements. BYN will never sell your personal data. See our
                Privacy Policy for full details.
              </p>

              <p className="mb-2">
                <strong>10. Indemnification:</strong> You agree to indemnify and
                hold harmless BYN, its affiliates, employees, and directors from
                any claims, liabilities, damages, and expenses arising out of
                your use of the platform or violation of these terms.
              </p>

              <p className="mb-2">
                <strong>11. Termination:</strong> BYN reserves the right to
                suspend or terminate your account at any time for violations of
                these terms, fraud, abuse, or any activity deemed harmful to the
                platform.
              </p>

              <p className="mb-2">
                <strong>12. Governing Law:</strong> These terms are governed by
                the laws of British Columbia, Canada. Any legal action must be
                brought in the courts of British Columbia.
              </p>

              <p className="mb-4">
                For any legal inquiries, contact us at{" "}
                <strong>legal@bookyourneed.com</strong>.
              </p>

              <div
                className="text-blue-600 font-semibold cursor-pointer underline mt-2"
                onClick={() => setShowFullTerms(false)}
              >
                Hide Full Terms ▲
              </div>
            </>
          )}
        </div>

        {/* Agree Checkbox */}
        <div className="mt-6 flex items-center gap-3">
          <input
            type="checkbox"
            id="agree"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="w-5 h-5"
          />
          <label htmlFor="agree" className="text-sm text-gray-700">
            I agree to the Terms & Conditions
          </label>
        </div>

        {/* Continue Button */}
        <button
          onClick={handleAgree}
          disabled={!agreed}
          className={`w-full mt-6 py-3 rounded-xl font-bold text-white transition-all ${
            agreed
              ? "bg-gradient-to-r from-purple-600 to-blue-500 hover:from-blue-700 hover:to-purple-700"
              : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          Continue →
        </button>
      </div>
    </div>
  );
};

export default TermsAndConditions;
